/**
 * The authed layout — a PATHLESS route, so `/today` stays `/today`.
 *
 * `beforeLoad` resolves the session through the query cache
 * (`ensureQueryData`), which means the guard and the shell's `useSessionQuery`
 * share one request rather than racing two. A missing session redirects to
 * `/login` carrying the attempted href, so signing in returns the user to where
 * they were going.
 */

import { createFileRoute, redirect } from '@tanstack/react-router';
import { fetchSession } from '../api/auth.ts';
import { queryKeys } from '../api/keys.ts';
import { AppShell } from '../components/app-shell.tsx';
import { RouteErrorPanel } from '../components/route-error-boundary.tsx';

export const Route = createFileRoute('/_authed')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData({
      queryKey: queryKeys.session(),
      queryFn: fetchSession,
      // A stale-but-present session is fine for a route guard; the API is the
      // real gate and answers 401 if it disagrees.
      staleTime: 30_000,
      retry: false,
    });
    if (session === null) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    return { session };
  },
  // `AppShell` renders the router `<Outlet />` itself, between the nav and the
  // connection strip.
  component: AppShell,
  errorComponent: RouteErrorPanel,
});
